import { ReactComponent as EditIcon } from '../assets/icons/edit-dark.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete-outlined.svg';
import { ReactComponent as PublishIcon } from '../assets/icons/public-dark.svg';
import { ReactComponent as UnpublishIcon } from '../assets/icons/domain_disabled-dark.svg';
import { FIELD_TYPES } from '../constants/fieldTypes';
import {
  ANNOUNCEMENT_CATEGORY_KEYS,
  ANNOUNCEMENT_FORM_FIELD_NAMES,
  ANNOUNCEMENT_ATTACHMENT_MAX_SIZE,
  ANNOUNCEMENT_ATTACHMENT_MIME_TYPES,
  ANNOUNCEMENT_ATTACHMENT_TYPES,
} from '../constants/announcements';
import {
  forbiddenCharacters,
  isEmpty,
  isRichTextEditorEmpty,
  validateFileSize,
  validateFileType,
  validateLength,
} from './validation';
import { isObjectEmpty } from './helpers';

export const isAnnouncementPublishedCheck = (announcement = null) => {
  if (!announcement) {
    return true;
  }

  return !!announcement.is_published;
};

const ANNOUNCEMENT_ATTACHMENT_TYPE_LABELS = {
  PDF: 'PDF',
  DOC: 'Word',
  DOCX: 'Word',
  CSV: 'CSV',
};

export const prepareAnnouncementAttachmentNameFromUrl = attachmentUrl => {
  if (!attachmentUrl || typeof attachmentUrl !== 'string') {
    return '';
  }

  const fileName = attachmentUrl.split('?')[0].split('/').pop() || '';

  if (!fileName) {
    return '';
  }

  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
};

export const prepareAnnouncementAttachmentInfo = attachment => {
  if (!attachment || typeof attachment !== 'string') {
    return null;
  }

  const name = prepareAnnouncementAttachmentNameFromUrl(attachment);
  const extension = name.split('.').pop()?.toUpperCase() || '';
  const typeLabel = ANNOUNCEMENT_ATTACHMENT_TYPE_LABELS[extension] || extension;

  return {
    name,
    typeLabel,
    url: attachment,
  };
};

export const prepareAnnouncementAttachmentInitialValue = (
  announcement = null
) => {
  if (!announcement?.attachment) {
    return null;
  }

  return announcement.attachment;
};

const getValidAnnouncementAttachmentValue = value => {
  if (
    value instanceof File &&
    !validateFileType(value, ANNOUNCEMENT_ATTACHMENT_MIME_TYPES)
  ) {
    return '';
  }

  return value || '';
};

export const isValidAnnouncementCategory = categoryKey =>
  ANNOUNCEMENT_CATEGORY_KEYS.includes(categoryKey.toUpperCase());

export const prepareAnnouncementCategoryPath = categoryKey =>
  `/announcements/${categoryKey.toLowerCase()}`;

export const prepareAnnouncementMenuItems = (
  labels,
  announcement,
  onEdit,
  onDelete,
  onPublish,
  onUnpublish
) => {
  const isPublished = isAnnouncementPublishedCheck(announcement);

  return [
    {
      type: 'edit',
      label: labels.edit,
      shouldItemRender: () => !isPublished,
      action: () => onEdit(announcement),
      icon: EditIcon,
    },
    {
      type: isPublished ? 'unpublish' : 'publish',
      label: isPublished ? labels.unpublish : labels.publish,
      shouldItemRender: () => true,
      action: () =>
        isPublished ? onUnpublish(announcement) : onPublish(announcement),
      icon: isPublished ? UnpublishIcon : PublishIcon,
    },
    {
      type: 'delete',
      label: labels.delete,
      shouldItemRender: () => true,
      action: () => onDelete(announcement),
      icon: DeleteIcon,
    },
  ];
};

export const prepareAnnouncementFormInitialData = (
  categoryKey,
  announcement = null
) => ({
  ...(announcement?.id ? { id: announcement.id } : {}),
  [ANNOUNCEMENT_FORM_FIELD_NAMES.CATEGORY]:
    announcement?.category || categoryKey,
  [ANNOUNCEMENT_FORM_FIELD_NAMES.TITLE]: announcement?.title || '',
  [ANNOUNCEMENT_FORM_FIELD_NAMES.BODY]: announcement?.body || '',
  [ANNOUNCEMENT_FORM_FIELD_NAMES.IS_PUBLISHED]:
    isAnnouncementPublishedCheck(announcement),
  [ANNOUNCEMENT_FORM_FIELD_NAMES.ATTACHMENT]:
    prepareAnnouncementAttachmentInitialValue(announcement),
});

export const prepareAnnouncementCreateFormData = values => {
  const { CATEGORY, TITLE, BODY, IS_PUBLISHED, ATTACHMENT } =
    ANNOUNCEMENT_FORM_FIELD_NAMES;
  const formData = new FormData();

  formData.append(CATEGORY, values[CATEGORY] || '');
  formData.append(TITLE, values[TITLE] || '');
  formData.append(BODY, values[BODY] || '');
  formData.append(IS_PUBLISHED, values[IS_PUBLISHED] || false);
  formData.append(
    ATTACHMENT,
    getValidAnnouncementAttachmentValue(values[ATTACHMENT])
  );

  return formData;
};

export const prepareAnnouncementPatchFormData = (values, announcement) => {
  const { TITLE, BODY, IS_PUBLISHED, ATTACHMENT } =
    ANNOUNCEMENT_FORM_FIELD_NAMES;
  const formData = new FormData();
  const changedFields = {};

  if (values[TITLE] !== announcement?.title) {
    changedFields[TITLE] = values[TITLE] || '';
  }

  if (values[BODY] !== announcement?.body) {
    changedFields[BODY] = values[BODY] || '';
  }

  if (values[IS_PUBLISHED] !== isAnnouncementPublishedCheck(announcement)) {
    changedFields[IS_PUBLISHED] = values[IS_PUBLISHED] || false;
  }

  const initialAttachment =
    prepareAnnouncementAttachmentInitialValue(announcement);
  const attachment = values[ATTACHMENT];
  const isInvalidAttachmentFile =
    attachment instanceof File &&
    !validateFileType(attachment, ANNOUNCEMENT_ATTACHMENT_MIME_TYPES);
  const isAttachmentChanged =
    !isInvalidAttachmentFile &&
    (attachment instanceof File || attachment !== initialAttachment);

  if (isObjectEmpty(changedFields) && !isAttachmentChanged) {
    return null;
  }

  Object.entries(changedFields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  if (isAttachmentChanged) {
    formData.append(
      ATTACHMENT,
      getValidAnnouncementAttachmentValue(attachment)
    );
  }

  return formData;
};

export const prepareAnnouncementFormFields = classes => [
  {
    name: ANNOUNCEMENT_FORM_FIELD_NAMES.TITLE,
    type: FIELD_TYPES.TEXT,
    translationKey: 'title',
    required: true,
    validators: [
      {
        type: 'required',
        validator: isEmpty,
      },
      {
        type: 'forbiddenCharacters',
        validator: forbiddenCharacters,
      },
      {
        type: 'titleMaxLength',
        validator: fieldValue => validateLength(fieldValue, 0, 255),
      },
    ],
  },
  {
    name: ANNOUNCEMENT_FORM_FIELD_NAMES.BODY,
    type: FIELD_TYPES.RICH_TEXT_EDITOR,
    translationKey: 'description',
    fieldWrapperClass: classes.description,
    required: true,
    validators: [
      {
        type: 'required',
        validator: value => isRichTextEditorEmpty(value),
      },
      {
        type: 'descriptionMaxLength',
        validator: fieldValue => validateLength(fieldValue, 0, 10000),
      },
    ],
  },
  {
    name: ANNOUNCEMENT_FORM_FIELD_NAMES.ATTACHMENT,
    type: FIELD_TYPES.UPLOAD_FILE,
    translationKey: 'uploadFile',
    isMultiple: false,
    types: ANNOUNCEMENT_ATTACHMENT_TYPES,
    shouldRemoveFieldError: true,
    validators: [
      {
        type: 'fileType',
        validator: value =>
          validateFileType(value, ANNOUNCEMENT_ATTACHMENT_MIME_TYPES),
      },
      {
        type: 'fileLength',
        validator: value => {
          if (!value) {
            return true;
          }

          const fileName = value instanceof File ? value.name : value;

          return validateLength(fileName, 0, 100);
        },
      },
      {
        type: 'fileSize',
        validator: value =>
          validateFileSize(value, ANNOUNCEMENT_ATTACHMENT_MAX_SIZE),
      },
    ],
  },
  {
    name: ANNOUNCEMENT_FORM_FIELD_NAMES.IS_PUBLISHED,
    type: FIELD_TYPES.SWITCH,
    translationKey: 'published',
    labelClass: classes.switchLabel,
  },
];
